import { useCallback, useEffect, useState } from 'react';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { execApi } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import './Reports.css';

// מחולל דוחות (Avi 2026-08-05, screen Ohad 2026-08-15): pick a report from the
// catalogue, choose recipients + cadence, and the engine mails it on schedule.
// The screen leans on two trust anchors: PREVIEW shows the exact mail before
// anyone subscribes, and the RUN LEDGER shows every send that actually happened.

interface Report { id: string; name: string; description: string; composable?: boolean }
interface Block { id: string; name: string; description: string }
interface Section { block: string; period?: string; role?: string }
interface CustomParams { title?: string; sections?: Section[] }
interface Sub {
  sub_id: string; report_id: string; recipients: string[]; cadence: string;
  send_hour: number; weekdays: number[]; day_of_month: number | null;
  params?: CustomParams; active: boolean;
}
interface Run {
  run_id: string; report_id: string; slot: string; recipients: string[];
  status: string; error: string | null; actor: string; ts: string;
}
interface SchedulerCfg { enabled: boolean; owner: string; this_env: string; active_here: boolean }
interface Recipient { email: string; name: string }

const DAY_NAMES = ['שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת', 'ראשון']; // Python: Monday=0..Sunday=6

function cadenceLabel(s: Sub): string {
  const hh = `${String(s.send_hour).padStart(2, '0')}:00`;
  if (s.cadence === 'daily') return `כל יום ב-${hh}`;
  if (s.cadence === 'weekly') {
    const days = (s.weekdays || []).map((d) => DAY_NAMES[d]).join(', ');
    return `שבועי (${days || '—'}) ב-${hh}`;
  }
  return `חודשי, ב-${s.day_of_month || 1} לחודש ב-${hh}`;
}

export default function ExecReportsPage() {
  const { can } = usePermissions();
  const editable = can('reports', 'edit');

  const [reports, setReports] = useState<Report[]>([]);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [periods, setPeriods] = useState<Record<string, string>>({});
  const [roles, setRoles] = useState<Record<string, string>>({});
  const [scheduler, setScheduler] = useState<SchedulerCfg | null>(null);
  const [people, setPeople] = useState<Recipient[]>([]);
  const [subs, setSubs] = useState<Sub[]>([]);
  const [runs, setRuns] = useState<Run[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [editSub, setEditSub] = useState<Partial<Sub> | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      execApi.getReportsCatalog(),
      execApi.getReportSubscriptions(),
      execApi.getReportRuns(),
    ])
      .then(([cat, s, r]) => {
        setReports(cat.data.reports || []);
        setBlocks(cat.data.blocks || []);
        setPeriods(cat.data.periods || {});
        setRoles(cat.data.roles || {});
        setScheduler(cat.data.scheduler || null);
        setSubs(s.data.subscriptions || []);
        setRuns(r.data.runs || []);
      })
      .catch(() => setError('הטעינה נכשלה — נסו לרענן'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // The recipient pick-list, straight from user management. Loaded once —
  // it feeds the subscription editor.
  useEffect(() => {
    execApi.getReportRecipients()
      .then((res) => setPeople(res.data.recipients || []))
      .catch(() => setPeople([]));
  }, []);

  const toggleScheduler = () => {
    if (!scheduler) return;
    setBusy(true);
    execApi.setReportsScheduler(!scheduler.enabled, scheduler.owner || 'prod')
      .then((res) => { setScheduler(res.data.scheduler); flash(res.data.scheduler.enabled ? 'המתזמן הופעל ✓' : 'המתזמן כובה'); })
      .catch(() => setError('עדכון המתזמן נכשל'))
      .finally(() => setBusy(false));
  };

  const nameOf = (rid: string) => reports.find((r) => r.id === rid)?.name || rid;

  const flash = (msg: string) => { setNotice(msg); setTimeout(() => setNotice(''), 4000); };

  const preview = (rid: string, params?: CustomParams) => {
    setPreviewHtml('');
    execApi.getReportPreview(rid, params)
      .then((res) => setPreviewHtml(String(res.data)))
      .catch(() => { setPreviewHtml(null); setError('התצוגה המקדימה נכשלה'); });
  };

  const sendNow = (rid: string, params?: CustomParams) => {
    setBusy(true);
    execApi.sendReportNow(rid, undefined, params)
      .then((res) => { flash(`נשלח אליך ✓ (${(res.data.sent_to || []).join(', ')})`); load(); })
      .catch((e) => setError(e?.response?.data?.detail || 'השליחה נכשלה'))
      .finally(() => setBusy(false));
  };

  const saveSub = (s: Partial<Sub>, deleted = false) => {
    setBusy(true);
    execApi.saveReportSubscription({
      sub_id: s.sub_id || null,
      report_id: s.report_id || reports[0]?.id,
      recipients: s.recipients || [],
      cadence: s.cadence || 'daily',
      send_hour: s.send_hour ?? 8,
      weekdays: s.weekdays || [],
      day_of_month: s.day_of_month ?? null,
      ...(s.params ? { params: s.params } : {}),
      active: s.active ?? true,
      deleted,
    })
      .then((res) => { setSubs(res.data.subscriptions || []); setEditSub(null); flash(deleted ? 'המנוי הוסר' : 'המנוי נשמר ✓'); })
      .catch((e) => setError(e?.response?.data?.detail || 'השמירה נכשלה'))
      .finally(() => setBusy(false));
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden" dir="rtl">
        <Header title="מחולל דוחות" />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="reports-wrap">
            {error && <div className="reports-error" onClick={() => setError('')}>{error}</div>}
            {notice && <div className="reports-notice">{notice}</div>}
            {scheduler && (
              <div className={`reports-scheduler ${scheduler.enabled ? 'is-on' : ''}`}>
                <div>
                  <div className="reports-scheduler-title">
                    {scheduler.enabled ? '🟢 המתזמן פועל' : '⚪ המתזמן כבוי'}
                  </div>
                  <div className="reports-scheduler-sub">
                    מתג אחד לשתי הסביבות · השליחה בפועל מסביבת {scheduler.owner === 'prod' ? 'פרוד' : 'דב'} בלבד
                    (מנעול כפול נגד מייל כפול) · "שלח עכשיו" עובד תמיד
                  </div>
                </div>
                {editable && (
                  <button type="button" className={`reports-btn ${scheduler.enabled ? '' : 'reports-btn-primary'}`}
                    disabled={busy} onClick={toggleScheduler}>
                    {scheduler.enabled ? 'כבה' : 'הפעל'}
                  </button>
                )}
              </div>
            )}

            <h2 className="reports-h2">הדוחות הזמינים</h2>
            <div className="reports-cards">
              {loading && [0, 1].map((i) => <div key={i} className="reports-card reports-sk" />)}
              {reports.map((r) => (
                <div key={r.id} className="reports-card">
                  <div className="reports-card-name">{r.name}</div>
                  <div className="reports-card-desc">{r.description}</div>
                  <div className="reports-card-actions">
                    {/* a composable report has no meaning without its sections —
                        everything starts from the builder */}
                    {!r.composable && (
                      <button type="button" className="reports-btn" onClick={() => preview(r.id)}>תצוגה מקדימה</button>
                    )}
                    {editable && !r.composable && (
                      <>
                        <button type="button" className="reports-btn" disabled={busy} onClick={() => sendNow(r.id)}>
                          שלח לי עכשיו
                        </button>
                        <button type="button" className="reports-btn reports-btn-primary"
                          onClick={() => setEditSub({ report_id: r.id, cadence: 'daily', send_hour: 8, recipients: [], active: true })}>
                          ＋ מנוי חדש
                        </button>
                      </>
                    )}
                    {editable && r.composable && (
                      <button type="button" className="reports-btn reports-btn-primary"
                        onClick={() => setEditSub({
                          report_id: r.id, cadence: 'daily', send_hour: 8, recipients: [], active: true,
                          params: { title: '', sections: [{ block: 'meetings', period: 'last_7', role: 'all' }] },
                        })}>
                        ➕ בנה דוח
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <h2 className="reports-h2">מנויים</h2>
            {!subs.length && !loading && <div className="reports-empty">אין עדיין מנויים — צרו אחד מכרטיס הדוח למעלה.</div>}
            {subs.map((s) => (
              <div key={s.sub_id} className={`reports-sub ${s.active ? '' : 'is-paused'}`}>
                <div>
                  <div className="reports-sub-name">
                    {s.report_id === 'custom' && s.params?.title ? s.params.title : nameOf(s.report_id)}
                    {s.report_id === 'custom' && (
                      <span className="reports-custom-tag">
                        מותאם · {(s.params?.sections || []).length} רובריקות
                      </span>
                    )}
                    {!s.active && <span className="reports-paused-tag">מושהה</span>}
                  </div>
                  <div className="reports-sub-meta">{cadenceLabel(s)} · {s.recipients.join(', ')}</div>
                </div>
                {editable && (
                  <div className="reports-sub-actions">
                    <button type="button" className="reports-link" onClick={() => preview(s.report_id, s.params)}>תצוגה</button>
                    <button type="button" className="reports-link" disabled={busy} onClick={() => sendNow(s.report_id, s.params)}>שלח לי</button>
                    <button type="button" className="reports-link" onClick={() => setEditSub(s)}>עריכה</button>
                    <button type="button" className="reports-link" disabled={busy}
                      onClick={() => saveSub({ ...s, active: !s.active })}>
                      {s.active ? 'השהה' : 'הפעל'}
                    </button>
                    <button type="button" className="reports-link reports-danger" disabled={busy}
                      onClick={() => saveSub(s, true)}>הסר</button>
                  </div>
                )}
              </div>
            ))}

            <h2 className="reports-h2">שליחות אחרונות</h2>
            {!runs.length && !loading && <div className="reports-empty">עדיין לא נשלח דבר.</div>}
            {runs.slice(0, 10).map((r) => (
              <div key={r.run_id} className="reports-run">
                <span className={`reports-run-dot ${r.status === 'ok' ? 'ok' : 'err'}`} />
                <span className="reports-run-when">{(r.ts || '').slice(0, 16).replace('T', ' ')}</span>
                <span>{nameOf(r.report_id)}</span>
                <span className="reports-run-to">→ {r.recipients.join(', ')}</span>
                {r.status !== 'ok' && <span className="reports-run-err">{r.error}</span>}
              </div>
            ))}
          </div>
        </main>
      </div>

      {previewHtml !== null && (
        <div className="reports-overlay" onClick={() => setPreviewHtml(null)}>
          <div className="reports-preview" onClick={(e) => e.stopPropagation()}>
            {previewHtml === ''
              ? <div className="reports-empty" style={{ padding: 24 }}>טוען תצוגה מקדימה…</div>
              : <iframe title="preview" srcDoc={previewHtml} className="reports-preview-frame" />}
            <button type="button" className="reports-btn" onClick={() => setPreviewHtml(null)}>סגור</button>
          </div>
        </div>
      )}

      {editSub && (
        <SubEditor
          sub={editSub}
          reports={reports}
          blocks={blocks}
          periods={periods}
          roles={roles}
          people={people}
          busy={busy}
          onPreview={(rid, params) => preview(rid, params)}
          onSave={(s) => saveSub(s)}
          onClose={() => setEditSub(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- sub editor

function SubEditor({ sub, reports, blocks, periods, roles, people, busy, onPreview, onSave, onClose }: {
  sub: Partial<Sub>; reports: Report[]; blocks: Block[];
  periods: Record<string, string>; roles: Record<string, string>;
  people: Recipient[]; busy: boolean;
  onPreview: (rid: string, params?: CustomParams) => void;
  onSave: (s: Partial<Sub>) => void; onClose: () => void;
}) {
  const [s, setS] = useState<Partial<Sub>>({ ...sub });
  const isCustom = s.report_id === 'custom';
  const sections = s.params?.sections || [];

  const setSections = (next: Section[]) =>
    setS((prev) => ({ ...prev, params: { ...(prev.params || {}), sections: next } }));
  const patchSection = (i: number, patch: Partial<Section>) =>
    setSections(sections.map((sec, j) => (j === i ? { ...sec, ...patch } : sec)));
  // Picked from user management + free-text extras (an address that has no
  // dashboard account is still a legitimate recipient).
  const [picked, setPicked] = useState<string[]>(
    (sub.recipients || []).filter((r) => people.some((p) => p.email === r)),
  );
  const [emails, setEmails] = useState(
    (sub.recipients || []).filter((r) => !people.some((p) => p.email === r)).join(', '),
  );

  const set = (patch: Partial<Sub>) => setS((prev) => ({ ...prev, ...patch }));
  const toggleDay = (d: number) => {
    const cur = s.weekdays || [];
    set({ weekdays: cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d] });
  };
  const togglePerson = (email: string) =>
    setPicked((cur) => (cur.includes(email) ? cur.filter((e) => e !== email) : [...cur, email]));
  const extras = emails.split(/[,;\s]+/).map((e) => e.trim()).filter(Boolean);
  const recipients = Array.from(new Set([...picked, ...extras]));

  return (
    <div className="reports-overlay" onClick={onClose}>
      <div className="reports-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{s.sub_id ? 'עריכת מנוי' : isCustom ? 'בניית דוח מותאם' : 'מנוי חדש'}</h3>
        {!isCustom && (
          <label className="reports-field">
            דוח
            <select value={s.report_id} onChange={(e) => set({ report_id: e.target.value })}>
              {reports.filter((r) => !r.composable).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </label>
        )}
        {isCustom && (
          <>
            <label className="reports-field">
              שם הדוח
              <input type="text" value={s.params?.title || ''}
                onChange={(e) => setS((prev) => ({ ...prev, params: { ...(prev.params || {}), title: e.target.value } }))}
                placeholder='למשל: "סיכום שבועי — מכבי"' />
            </label>
            <div className="reports-field">
              רובריקות הדוח
              {sections.map((sec, i) => (
                <div key={i} className="reports-section-row">
                  <div className="reports-section-head">
                    <span className="reports-section-num">{i + 1}</span>
                    <select value={sec.block}
                      onChange={(e) => patchSection(i, e.target.value === 'meetings'
                        ? { block: 'meetings', period: sec.period || 'last_7', role: sec.role || 'all' }
                        : { block: e.target.value, period: undefined, role: undefined })}>
                      {blocks.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                    {sections.length > 1 && (
                      <button type="button" className="reports-link reports-danger"
                        onClick={() => setSections(sections.filter((_, j) => j !== i))}>הסר</button>
                    )}
                  </div>
                  {sec.block === 'meetings' && (
                    <div className="reports-section-params">
                      <select value={sec.period || 'last_7'} onChange={(e) => patchSection(i, { period: e.target.value })}>
                        {Object.entries(periods).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                      <select value={sec.role || 'all'} onChange={(e) => patchSection(i, { role: e.target.value })}>
                        {Object.entries(roles).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              ))}
              <button type="button" className="reports-btn reports-add-section"
                onClick={() => setSections([...sections, { block: 'meetings', period: 'last_7', role: 'all' }])}>
                ＋ הוסף רובריקה
              </button>
              <button type="button" className="reports-btn"
                onClick={() => onPreview('custom', s.params)}>
                תצוגה מקדימה
              </button>
            </div>
          </>
        )}
        <div className="reports-field">
          נמענים — משתמשי המערכת
          <div className="reports-people">
            {!people.length && <span className="reports-empty">רשימת המשתמשים לא זמינה — אפשר להקליד מייל למטה.</span>}
            {people.map((p) => (
              <label key={p.email} className={`reports-person ${picked.includes(p.email) ? 'is-on' : ''}`}>
                <input type="checkbox" checked={picked.includes(p.email)} onChange={() => togglePerson(p.email)} />
                <span>{p.name}</span>
                <span className="reports-person-mail">{p.email}</span>
              </label>
            ))}
          </div>
        </div>
        <label className="reports-field">
          מיילים נוספים (לא חובה, מופרדים בפסיק)
          <textarea rows={1} dir="ltr" value={emails} onChange={(e) => setEmails(e.target.value)}
            placeholder="someone@taliazhealth.com" />
        </label>
        <div className="reports-field">
          תדירות
          <div className="reports-cadence">
            {(['daily', 'weekly', 'monthly'] as const).map((c) => (
              <label key={c}>
                <input type="radio" checked={(s.cadence || 'daily') === c} onChange={() => set({ cadence: c })} />
                {c === 'daily' ? 'יומי' : c === 'weekly' ? 'שבועי' : 'חודשי'}
              </label>
            ))}
          </div>
        </div>
        {s.cadence === 'weekly' && (
          <div className="reports-field">
            ימים
            <div className="reports-days">
              {/* draw Sunday-first for a Hebrew reader; values stay Monday=0..Sunday=6 */}
              {[6, 0, 1, 2, 3, 4, 5].map((d) => (
                <label key={d}>
                  <input type="checkbox" checked={(s.weekdays || []).includes(d)} onChange={() => toggleDay(d)} />
                  {DAY_NAMES[d]}
                </label>
              ))}
            </div>
          </div>
        )}
        {s.cadence === 'monthly' && (
          <label className="reports-field">
            יום בחודש
            <input type="number" min={1} max={28} value={s.day_of_month ?? 1}
              onChange={(e) => set({ day_of_month: Number(e.target.value) })} />
          </label>
        )}
        <label className="reports-field">
          שעת שליחה (שעון ישראל)
          <select value={s.send_hour ?? 8} onChange={(e) => set({ send_hour: Number(e.target.value) })}>
            {Array.from({ length: 24 }, (_, h) => (
              <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
            ))}
          </select>
        </label>
        <div className="reports-modal-foot">
          <button type="button" className="reports-btn reports-btn-primary"
            disabled={busy || !recipients.length}
            onClick={() => onSave({ ...s, recipients })}>
            שמור מנוי
          </button>
          <button type="button" className="reports-btn" onClick={onClose}>ביטול</button>
        </div>
      </div>
    </div>
  );
}
